import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  contentChildren,
  ElementRef,
  inject,
  input,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgbCollapse, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, take } from 'rxjs/operators';
import { StoresService } from 'src/app/services/stores.service';

import {
  ListViewRow,
  ListViewFilters,
  ListViewResults,
  ListViewSettings,
  ListViewTableColumn,
  ListViewTableColumnType,
} from '../../interfaces/list-view-models';
import { ServerResponse } from '../../interfaces/server-response';

import { LoaderComponent } from '../loader/loader.component';

import { ActionContentDirective } from './action-content.directive';
import { ExpandContentDirective } from './expand-content.directive';
import { ItemActionsContentDirective } from './item-actions-content.directive';
import { ItemLeadingActionsContentDirective } from './item-leading-actions-content.directive';
import { applyListViewDefaults } from './list-view-defaults';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, LoaderComponent, NgbCollapse, NgbTooltip, RouterModule],
  selector: 'app-list-view',
  standalone: true,
  templateUrl: './list-view.component.html',
})
export class ListViewComponent implements AfterViewInit, OnDestroy {
  readonly ListViewTableColumnType = ListViewTableColumnType;

  readonly actionsContentTpl = computed(
    this.firstActionsContentDirective.bind(this) as () => ActionContentDirective | null
  );
  readonly actionsContentTpls = contentChildren(ActionContentDirective);

  readonly cfg = computed(this.mergeCfg.bind(this) as () => ListViewSettings);

  data: ListViewRow[] = [];

  readonly expandContentTpl = computed(
    this.firstExpandContentDirective.bind(this) as () => ExpandContentDirective | null
  );
  readonly expandContentTpls = contentChildren(ExpandContentDirective);

  isGalleryMode = false;
  isLoading = true;

  readonly itemActionsContentTpl = computed(
    this.firstItemActionsContentDirective.bind(this) as () => ItemActionsContentDirective | null
  );
  readonly itemActionsContentTpls = contentChildren(ItemActionsContentDirective);
  readonly itemLeadingActionsContentTpl = computed(
    this.firstItemLeadingActionsContentDirective.bind(
      this
    ) as () => ItemLeadingActionsContentDirective | null
  );
  readonly itemLeadingActionsContentTpls = contentChildren(ItemLeadingActionsContentDirective);

  pageCount = 0;
  pageIndex = 0;
  pages: number[] = [];

  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  searchTerm = '';
  readonly settings = input.required<ListViewSettings>();
  shopifyStoresIdsThatHaveIntegration: string[] = [];
  sortColumn = '';
  sortDescending = false;

  private readonly cdr = inject(ChangeDetectorRef);
  private shopifyStoresSub?: Subscription;
  private readonly storesService = inject(StoresService);
  private readonly subsCleanup: Subscription[] = [];

  createItem(): void {
    const s = this.cfg();
    if (s.allowCreate && s.create) {
      s.create().subscribe((result: boolean) => {
        if (result) {
          this.refreshData();
        }
        this.cdr.markForCheck();
      });
    }
  }

  editItem(item: ListViewRow): boolean {
    const s = this.cfg();
    if (s.allowEdit && s.edit) {
      s.edit(item).subscribe((result: boolean) => {
        if (result) {
          this.refreshData();
        }
        this.cdr.markForCheck();
      });
    }

    return false;
  }

  expandItem(item: ListViewRow): void {
    item['expanded'] = !item['expanded'];
    item['loaded'] = true;
    this.cdr.markForCheck();
  }

  formatBoolean(value: unknown): string {
    if (typeof value !== 'boolean') {
      return '';
    }
    return value ? 'Yes' : 'No';
  }

  formatDate(date: unknown, column: ListViewTableColumn): string {
    if (!date) {
      return column.emptyText ?? '';
    }

    return '';
  }

  formatDateTime(dateTime: unknown, column: ListViewTableColumn): string {
    if (!dateTime) {
      return column.emptyText ?? '';
    }

    return '';
  }

  formatDefaultText(text: unknown, column: ListViewTableColumn): string {
    if (text === null || text === undefined || text === '') {
      return column.emptyText ?? '';
    }
    if (typeof text === 'number' && Number.isNaN(text)) {
      return column.emptyText ?? '';
    }
    return String(text);
  }

  getExpandColspan(): number {
    const s = this.cfg();
    let colspan = (s.tableSettings?.columns?.length ?? 0) + 1;

    if (s.showActiveState) {
      colspan++;
    }

    if (s.allowEdit || this.itemActionsContentTpl()) {
      colspan++;
    }

    if (this.itemLeadingActionsContentTpl()) {
      colspan++;
    }

    return colspan;
  }

  getImagePath(item: ListViewRow, columnName: string): string | null {
    const cell = item[columnName];
    if (cell && typeof cell === 'object' && 'path' in cell) {
      const path = (cell as { path?: unknown }).path;
      return typeof path === 'string' ? path : null;
    }
    return null;
  }

  getItemColumnLink(item: ListViewRow, column: ListViewTableColumn): unknown[] | null {
    if (column.linkFunc) {
      return column.linkFunc(item) as unknown[];
    }

    return null;
  }

  getItemGalleryLink(item: ListViewRow): unknown[] | null {
    const s = this.cfg();
    if (s.gallerySettings?.linkFunc) {
      return s.gallerySettings.linkFunc(item) as unknown[];
    }

    return null;
  }

  getShopifyStoreIdsThatHaveIntegration(): void {
    this.shopifyStoresSub?.unsubscribe();
    this.shopifyStoresSub = this.storesService
      .getShopifyStoreIdsThatHaveIntegration()
      .pipe(take(1))
      .subscribe((storesIds) => {
        this.shopifyStoresIdsThatHaveIntegration = storesIds;
        this.cdr.markForCheck();
      });
  }

  getSyncIconTooltip(storeDataitem: ListViewRow): string {
    const lastSync = this.getLatestStoreSyncTime({
      lastScheduledSyncTime: storeDataitem['lastScheduledSyncTime'] as number | undefined,
      lastSyncTime: storeDataitem['lastSyncTime'] as number | undefined,
    });
    const formattedDate =
      typeof lastSync === 'number' || typeof lastSync === 'string'
        ? new Date(lastSync).toLocaleString()
        : 'None';
    return `Sync Integration\n (Last Sync Time: ${formattedDate})`;
  }

  goToGallery(): void {
    if (this.cfg().allowGalleryMode) {
      window.scroll(0, 0);
      this.isGalleryMode = true;
      this.cdr.markForCheck();
    }
  }

  goToPage(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.refreshData();
  }

  goToTable(): void {
    if (this.cfg().allowTableMode) {
      window.scroll(0, 0);
      this.isGalleryMode = false;
      this.cdr.markForCheck();
    }
  }

  isShopifyIntegratedStore(row: ListViewRow): boolean {
    const id = row['id'];
    const idStr = typeof id === 'string' || typeof id === 'number' ? String(id) : '';
    return idStr !== '' && this.shopifyStoresIdsThatHaveIntegration.includes(idStr);
  }

  nextPage(): void {
    if (this.pageIndex < this.pageCount - 1) {
      this.goToPage(this.pageIndex + 1);
    }
  }

  ngAfterViewInit(): void {
    const s = this.cfg();
    if (s.allowGalleryMode && s.gallerySettings === undefined) {
      console.error('Gallery mode is enabled but no gallery settings were provided');
    }
    if (s.allowTableMode && s.tableSettings === undefined) {
      console.error('Table mode is enabled but no table settings were provided');
    }

    this.isGalleryMode = !!(s.allowGalleryMode && s.isGalleryDefault);
    this.sortColumn = s.defaultSortColumn;
    this.sortDescending = s.defaultSortDirection === 'desc';

    const inputEl = this.searchInput()?.nativeElement;
    if (inputEl && !s.hideSearch) {
      const sub = fromEvent(inputEl, 'keyup')
        .pipe(debounceTime(1000), distinctUntilChanged())
        .subscribe(() => {
          this.refreshData();
        });
      this.subsCleanup.push(sub);
    }

    this.loadData();
  }

  ngOnDestroy(): void {
    for (const currSub of this.subsCleanup) {
      currSub.unsubscribe();
    }
    this.shopifyStoresSub?.unsubscribe();
  }

  prevPage(): void {
    if (this.pageIndex > 0) {
      this.goToPage(this.pageIndex - 1);
    }
  }

  refreshData(): void {
    window.scroll(0, 0);
    this.loadData();
  }

  sortByColumn(column: ListViewTableColumn): void {
    if (column.allowSort) {
      if (this.sortColumn === column.name) {
        this.sortDescending = !this.sortDescending;
      } else {
        this.sortColumn = column.name;
        this.sortDescending = false;
      }

      this.refreshData();
    }
  }

  async syncIntergrationData(storeDataitem: ListViewRow): Promise<void> {
    const s = this.cfg();
    if (s.allowEdit && s.edit) {
      const previousLastSyncTime = storeDataitem['lastSyncTime'] as number | undefined;
      try {
        const id = storeDataitem['id'];
        if (typeof id === 'string' || typeof id === 'number') {
          await s.syncIntegrationData(String(id));
        }
        storeDataitem['lastSyncTime'] = Date.now();
      } catch (error) {
        storeDataitem['lastSyncTime'] = previousLastSyncTime;
        console.error(error);
      }
      this.cdr.markForCheck();
    }
  }

  private buildVisiblePageIndices(pageCount: number): number[] {
    if (pageCount <= 0) {
      return [];
    }
    const maxVisible = 10;
    if (pageCount <= maxVisible) {
      return Array.from({ length: pageCount }, (_, index) => index);
    }
    let end = this.pageIndex + maxVisible - 1;
    let start = this.pageIndex;
    if (end > pageCount - 1) {
      end = pageCount - 1;
      start = pageCount - maxVisible;
    }
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  private firstActionsContentDirective(): ActionContentDirective | null {
    return this.actionsContentTpls()[0] ?? null;
  }

  private firstExpandContentDirective(): ExpandContentDirective | null {
    return this.expandContentTpls()[0] ?? null;
  }

  private firstItemActionsContentDirective(): ItemActionsContentDirective | null {
    return this.itemActionsContentTpls()[0] ?? null;
  }

  private firstItemLeadingActionsContentDirective(): ItemLeadingActionsContentDirective | null {
    return this.itemLeadingActionsContentTpls()[0] ?? null;
  }

  private getFilters(): ListViewFilters {
    return {
      pageIndex: this.pageIndex,
      pageSize: this.getPageSize(),
      searchTerm: this.searchTerm,
      sortColumn: this.sortColumn,
      sortDescending: this.sortDescending,
    };
  }

  private getLatestStoreSyncTime(store: {
    lastScheduledSyncTime?: number;
    lastSyncTime?: number;
  }): number | undefined {
    const times = [store.lastSyncTime, store.lastScheduledSyncTime].filter(
      (time): time is number => time != null
    );

    return times.length > 0 ? Math.max(...times) : undefined;
  }

  private getPageSize(): number {
    if (this.isGalleryMode) {
      return this.cfg().allowCreate ? 19 : 20;
    }
    return 20;
  }

  private loadData(): void {
    this.isLoading = true;

    const filters = this.getFilters();
    this.getShopifyStoreIdsThatHaveIntegration();
    this.cfg()
      .getData(filters)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        error: (err: unknown) => {
          console.error('ListView getData failed', err);
          this.cdr.markForCheck();
        },
        next: (response: ServerResponse<ListViewResults<unknown>>) => {
          if (response.result && response.data) {
            this.pageCount = Math.ceil(response.data.dataCount / filters.pageSize);
            this.data = (response.data.data ?? []) as ListViewRow[];
            this.pages = this.buildVisiblePageIndices(this.pageCount);
          } else {
            console.error('No data found');
            this.data = [];
            this.pageCount = 0;
            this.pages = [];
          }
          this.cdr.markForCheck();
        },
      });
  }

  private mergeCfg(): ListViewSettings {
    return applyListViewDefaults(this.settings());
  }
}
